import { AggregatedNode } from "@p0security/shared/graph/aggregate";
import {
  AnyAggregates,
  AnyNode,
  AssessmentNodes,
} from "@p0security/shared/types/assessment/data/index";
import { ResizableTableColumnType } from "components/ResizableTable";

const SCOPE_HEADER = "scope";

export type NodeWithScope = {
  node: AggregatedNode<AssessmentNodes, keyof AssessmentNodes, AnyAggregates>;
  scope: string;
};

export type ExportColumnType<N extends AnyNode> =
  ResizableTableColumnType<N> & {
    export?: {
      toTsvCell: (node: N) => string;
      toJsonObject: (node: N) => any;
    };
  };

export const isExportColumn = (
  column: ResizableTableColumnType<AnyNode>
): column is ExportColumnType<any> & {
  export: NonNullable<ExportColumnType<any>["export"]>;
} => "export" in column && !!column.export;

export const toTsv =
  (
    exportable: (ExportColumnType<any> & {
      export: NonNullable<ExportColumnType<any>["export"]>;
    })[],
    includeScope: boolean
  ) =>
  async (nodes: NodeWithScope[]) => {
    const header = [
      ...exportable.map((col) => col.key),
      ...(includeScope ? [SCOPE_HEADER] : []),
    ];
    const rows = nodes.map((n) =>
      [
        ...exportable.map((col) => col.export.toTsvCell(n.node) || ""),
        ...(includeScope ? [n.scope] : []),
      ].join("\t")
    );
    return [header.join("\t"), ...rows].join("\n");
  };

export const toJson =
  (
    exportable: (ExportColumnType<any> & {
      export: NonNullable<ExportColumnType<any>["export"]>;
    })[],
    includeScope: boolean
  ) =>
  async (nodes: NodeWithScope[]) =>
    JSON.stringify(
      nodes.map((n) =>
        Object.fromEntries([
          ...exportable.map((col) => [
            col.key,
            col.export.toJsonObject(n.node),
          ]),
          ...(includeScope ? [[SCOPE_HEADER, n.scope]] : []),
        ])
      ),
      undefined,
      2
    );
